import React, { FunctionComponent } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { Colors, Typography, Avatar, Button, AnalyticsProps, AnalyticsComponent } from '@inplayer-org/inplayer-ui';
import { FaRegEdit } from 'react-icons/fa';
import { IoIosCheckmark, IoIosClose, IoMdTrash, IoIosLock } from 'react-icons/io';

// components
import { CustomCard } from 'components/CustomCard';
import Restricted from 'restrictions/roles/components/Restricted';
import { transparentize } from 'polished';
import { Label } from 'components/SharedStyledElements';

const TopInfo = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  border-bottom: 1px solid ${Colors.gray};
`;

const AvatarContainer = styled.div`
  border-right: 1px solid ${Colors.gray};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem 1.5rem;
  width: 50%;

  img {
    width: 90%;
  }
`;

const MemberInfo = styled.div`
  width: 50%;
  padding: 1.5rem 0;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
  * {
    @media screen and (max-width: 1480px) {
      font-size: ${({ theme }) => theme.font.sizes.small};
    }
  }
`;

const StyledTypography = styled(Typography)`
  font-weight: ${({ theme }) => theme.font.weights.normal};
  color: ${Colors.skyBlue};
  margin: 0;
`;

export const CardContent = styled.div`
  width: 100%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
`;

const HeaderWrapper = styled.div<{ displayTopHeader?: string }>`
  display: flex;
  justify-content: space-between;
  ${({ displayTopHeader }) => !displayTopHeader && 'align-self: flex-end'};
`;

const IconWrapper = styled(HeaderWrapper)`
  align-items: center;
  align-self: flex-end;
`;

const StyledIcon = styled(FaRegEdit)`
  font-size: 1.3rem;
  cursor: pointer;
  align-self: flex-end;
`;

const StyledButton = styled(Button)`
  align-self: center;
  width: 11rem;
`;

interface StyledReactIconProps {
  color: string;
}
export const StyledReactIcon = styled(IoIosCheckmark)<StyledReactIconProps>`
  width: 2.2rem;
  height: 2.2rem;
  color: ${({ color }) => color};
  cursor: pointer;
`;

interface CustomCardProps {
  marginBottom?: string;
}

export const StyledCard = styled(CustomCard)`
  ${({ marginBottom }: CustomCardProps) => marginBottom && `margin-bottom: ${marginBottom}`};
  align-self: flex-start;
  border-radius: 5px;
  border-top: 5px solid ${({ theme }) => theme.palette.primary.main};
  box-shadow: 0 0 8px 1px ${transparentize(0.87, Colors.fontDarkGray)};
`;

const dateFormat = 'DD-MM-YYYY';

interface Props {
  displayAvatarInfo?: boolean;
  marginBottom?: string;
  createdAt?: number;
  canEditField?: boolean;
  iconName: string;
  actionTitle: string;
  isEditing?: boolean;
  showModal: () => void;
  handleCancel: () => void;
  handleCheck: () => void;
  handleIconClick: () => void;
  isReadOnly?: boolean;
  displayTopHeader?: string;
  isAdminMerchants?: boolean;
}

const AudienceDetailsBox: FunctionComponent<Props> = ({
  displayAvatarInfo,
  children,
  marginBottom,
  createdAt,
  iconName,
  showModal,
  actionTitle,
  isEditing,
  handleCancel,
  handleIconClick,
  handleCheck,
  isReadOnly = false,
  canEditField = true,
  displayTopHeader,
  isAdminMerchants = false,
}) => {
  const isMetadata = displayTopHeader === 'metadata';
  const renderAvatarInfo = () => {
    const avatar = 'https://assets.inplayer.com/images/merchant-default-avatar.png';
    return (
      <TopInfo data-testid="avatar-info">
        <AvatarContainer>
          <Avatar imageUrl={avatar} />
        </AvatarContainer>
        <MemberInfo>
          <Label variant="p">Member from</Label>
          <StyledTypography variant="h6">{moment(createdAt! * 1000).format(dateFormat)}</StyledTypography>
        </MemberInfo>
      </TopInfo>
    );
  };

  const renderEditIcons = () => {
    if (isEditing) {
      return (
        <AnalyticsComponent>
          {({ pages, tracker, merchantId, ip }) => (
            <IconWrapper data-testid="is-editing">
              <StyledReactIcon
                data-testid="save"
                color={Colors.green}
                onClick={() => {
                  tracker.track({
                    event: 'click',
                    type: 'icon',
                    tag: isMetadata ? 'button_save_metadata' : 'button_save_general_information',
                    pages,
                    merchantId,
                    ip,
                  });
                  return handleCheck();
                }}
              />
              <StyledReactIcon
                data-testid="cancel"
                as={IoIosClose}
                color={Colors.red}
                onClick={() => {
                  tracker.track({
                    event: 'click',
                    type: 'icon',
                    tag: isMetadata ? 'button_cancel_changes_metadata' : 'button_cancel_general_information',
                    pages,
                    merchantId,
                    ip,
                  });
                  return handleCancel();
                }}
              />
            </IconWrapper>
          )}
        </AnalyticsComponent>
      );
    }
    return (
      <AnalyticsComponent>
        {({ pages, tracker, merchantId, ip }) => (
          <StyledIcon
            onClick={() => {
              tracker.track({
                event: 'click',
                type: 'icon',
                tag: isMetadata ? 'icon_edit_metadata' : 'icon_edit_general_information',
                pages,
                merchantId,
                ip,
              });
              return handleIconClick();
            }}
          />
        )}
      </AnalyticsComponent>
    );
  };

  let displayIcon;

  if (!isAdminMerchants) {
    displayIcon = iconName === 'trash' ? <IoMdTrash size={18} /> : <IoIosLock size={18} />;
  }

  return (
    <StyledCard direction="column" marginBottom={marginBottom}>
      {displayAvatarInfo && renderAvatarInfo()}
      <CardContent>
        <Restricted data-testid="can-edit" isRestricted={isReadOnly}>
          <HeaderWrapper displayTopHeader={displayTopHeader}>
            {displayTopHeader && (
              <Label title variant="p">
                {displayTopHeader}
              </Label>
            )}
            {canEditField && renderEditIcons()}
          </HeaderWrapper>
        </Restricted>
        {children}
        <Restricted isRestricted={isReadOnly}>
          <StyledButton
            tag={isMetadata ? 'button_clear_metadata' : 'button_reset_password'}
            onClick={showModal}
            icon={displayIcon}
          >
            {actionTitle}
          </StyledButton>
        </Restricted>
      </CardContent>
    </StyledCard>
  );
};

export default AudienceDetailsBox;
